'use client'

import * as Sentry from '@sentry/nextjs'
import { useMemo } from 'react'

import { EU_INSURANCE_COMPANIES } from '@/app/[locale]/order/_constants/eu-insurance-companies.const'

import { CartProductType } from '../../../../_context/order-flow-context'
import { canShowInsurance } from '../_utils/patient-form.domain'

import type { MSelectOption } from '@/_components/medante/m-select'
import type { ApiResponse, IInsuranceCompany, IOrderTimeSlotDetail } from '@/_types'

type UsePatientInsuranceParams = {
	primaryType: CartProductType | undefined
	timeslotDetail: ApiResponse<IOrderTimeSlotDetail> | undefined
	allInsuranceData: ApiResponse<IInsuranceCompany[]> | undefined
	watchedInsurance: string | undefined
}

/**
 * Resolves insurance dropdown options and whether the selected company is an EU insurer.
 */
export function usePatientInsurance({
	primaryType,
	timeslotDetail,
	allInsuranceData,
	watchedInsurance
}: UsePatientInsuranceParams) {
	const showInsurance = primaryType !== undefined && canShowInsurance(primaryType)

	const insuranceCompanies = useMemo(() => {
		if (primaryType === CartProductType.TEMPLATE) {
			return timeslotDetail?.data?.template?.insuranceCompanies ?? []
		}
		return allInsuranceData?.data.map(ins => ({ insuranceCompany: ins })) ?? []
	}, [primaryType, timeslotDetail, allInsuranceData])

	const insuranceSelectOptions = useMemo<MSelectOption[]>(
		() =>
			insuranceCompanies.map(ic => ({
				id: ic.insuranceCompany.id.toString(),
				name: ic.insuranceCompany.name
			})),
		[insuranceCompanies]
	)

	const selectedInsuranceCompany = useMemo(() => {
		if (!watchedInsurance) return null
		return (
			insuranceCompanies.find(ic => ic.insuranceCompany.id.toString() === watchedInsurance) ??
			null
		)
	}, [watchedInsurance, insuranceCompanies])

	const isEUInsurance = useMemo(() => {
		try {
			const code = selectedInsuranceCompany?.insuranceCompany?.code
			if (!code) return false
			return (EU_INSURANCE_COMPANIES as readonly string[]).includes(code)
		} catch (err) {
			Sentry.captureException(err)
			return false
		}
	}, [selectedInsuranceCompany])

	return { showInsurance, insuranceSelectOptions, isEUInsurance }
}
